import { Box, Grid, Typography, LinearProgress, Stack, Button } from '@mui/material';
import React from 'react';
import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { RenderHtml } from 'components/RenderHtml';
import OrganizationChip from 'components/OrganizationChip';
import { BackButton } from 'retired/BackButtonLayout/BackButton';
import { mediumGray } from 'utils/color-styles';
import { selectUser } from 'entities/user/selectors';
import { Features } from 'utils/features';
import { Privileges } from 'utils/privileges';
import { useActionTemplateResultsQuery } from 'generated/graphql';
import { capitalizeFirstLetter } from 'utils/stringFormat';

import { ActionTemplate } from '../types';
import { ActionTemplateCardImage } from '../ActionTemplateCard/ActionTemplateCardImage';
import { RewardChip } from '../ActionTemplateCard/RewardChip';
import { CreateActionFromTemplateButton } from '../ActionTemplateCard/CreateActionFromTemplateButton';
import { EditActionTemplateButton } from '../ActionTemplateCard/EditActionTemplateButton';

const ActionTemplatePreview = (): JSX.Element => {
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const user = useSelector(selectUser);
  const isActionsEnabled =
    user && user.hasFeature(Features.ACTIONS) && user.hasPrivilege(Privileges.ACTIONS_MANAGE);

  if (!isActionsEnabled) {
    history.replace('/');
  }

  const { data, loading, error } = useActionTemplateResultsQuery({
    variables: {
      templateId: id,
    },
    fetchPolicy: 'no-cache',
  });

  const actionTemplates = data?.actionTemplateResults?.templates ?? [];
  if (!loading && !actionTemplates.length) {
    return <div>Unable to load the template.</div>;
  }

  const actionTemplate: ActionTemplate = actionTemplates[0];

  return (
    <>
      {error ? (
        <div>There was an error loading this page. Please try again.</div>
      ) : loading ? (
        <LinearProgress />
      ) : (
        <>
          <Helmet title={actionTemplate.title} />
          <Box mb={10}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box display="flex">
                <BackButton
                  onClick={() => {
                    history.push('/actions/templates/browse');
                  }}
                  style={{ marginRight: 16 }}
                />
                <Typography fontSize={32} style={{ marginRight: 16 }}>
                  {actionTemplate.title}
                </Typography>
              </Box>

              <Stack direction="row" spacing={2}>
                <EditActionTemplateButton actionTemplate={actionTemplate} user={user} />
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    history.push('/actions/template/new');
                  }}
                  style={{ margin: '0 0 8px 8px' }}
                >
                  <>New Template</>
                </Button>
                <CreateActionFromTemplateButton actionTemplate={actionTemplate} />
              </Stack>
            </Box>

            <Grid container spacing={4}>
              <Grid item md={8}>
                <ActionTemplateCardImage actionTemplate={actionTemplate} />

                {actionTemplate.subTitle && (
                  <>
                    <Typography mt={2} fontSize={20}>
                      Subtitle
                    </Typography>
                    <Box fontSize={14}>
                      <RenderHtml source={{ html: actionTemplate.subTitle }} />
                    </Box>
                  </>
                )}

                {actionTemplate.description && (
                  <>
                    <Typography mt={2} fontSize={20}>
                      Description
                    </Typography>
                    <Box fontSize={14}>
                      <RenderHtml source={{ html: actionTemplate.description }} />
                    </Box>
                  </>
                )}
              </Grid>

              <Grid item md={4}>
                <Box
                  bgcolor="white"
                  borderRadius="12px"
                  border="1px solid"
                  borderColor={mediumGray}
                  padding={3}
                >
                  <Box>
                    <Typography mb={1}>Status:</Typography>
                    <OrganizationChip
                      size="small"
                      organization={{
                        name: capitalizeFirstLetter(actionTemplate.status),
                      }}
                    />
                  </Box>

                  {actionTemplate.Causes?.length ? (
                    <Box py={2} borderTop={`1px solid ${mediumGray}`}>
                      <Typography mb={1}>Causes:</Typography>
                      {actionTemplate.Causes.map((cause) => (
                        <OrganizationChip
                          key={cause.id}
                          size="small"
                          organization={{ name: cause.name }}
                        />
                      ))}
                    </Box>
                  ) : null}

                  {actionTemplate.incentives?.length && (
                    <Box py={2} borderTop={`1px solid ${mediumGray}`}>
                      <Typography>Reward: </Typography>
                      <RewardChip actionTemplate={actionTemplate} isAbsolute={false} />
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </>
  );
};

export default ActionTemplatePreview;
